/**
 * "O que fazer agora": insights derivados dos numeros, ordenados por urgencia.
 *
 * Decisoes nao obvias:
 *  1. Cada insight e uma FUNCAO PURA que devolve `null` quando o dado nao o
 *     sustenta. Nada e inventado para preencher a lista: com tudo saudavel a
 *     tela mostra um so item dizendo isso.
 *  2. Todo texto carrega o numero que o justifica (quantos, quanto, de que
 *     para que). Insight sem numero e opiniao.
 *  3. A prioridade e um inteiro fixo por tipo (0 = mais urgente). Funil
 *     pausado e gente esperando o comercial vem antes de "melhor horario":
 *     primeiro o que perde dinheiro hoje, depois o que otimiza amanha.
 *  4. Nenhum insight sugere agir ANTES do fato (lembrar de renovar, avisar
 *     antes do vencimento). O motor so entra depois do abandono ou da
 *     expiracao; e dai que vem a comissao (decisao do dono, 24/09/2026).
 */

import type { BadgeTone, IconName } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, formatRatio } from '../../../lib/format';
import type { FunnelKind } from '../../../types';
import { CHANNEL_LABEL_FULL, type FunnelsSummary, type LiveSnapshot, type OfferSummary } from '../types';
import { caiuEmPontos, subiuEmPontos } from './comparacao';
import { faixaDaHora } from './tempo';

export interface Insight {
  readonly id: string;
  /** 0 = mais urgente. */
  readonly prioridade: number;
  readonly tone: BadgeTone;
  readonly icone: IconName;
  readonly titulo: string;
  readonly texto: string;
  readonly acao: { readonly rotulo: string; readonly rota: string } | null;
}

const MAXIMO_DE_INSIGHTS = 5;
const QUEDA_TOTAL_BPS = 200;
const ALTA_TOTAL_BPS = 200;

const NOME_DO_FUNIL: Readonly<Record<FunnelKind, string>> = {
  carrinho_abandonado: 'carrinho abandonado',
  expirados: 'expirados',
};

const ROTA_DO_FUNIL: Readonly<Record<FunnelKind, string>> = {
  carrinho_abandonado: '/funis/carrinho',
  expirados: '/funis/expirados',
};

type Gerador = (resumo: FunnelsSummary, agora: LiveSnapshot | null, responsaveis: Readonly<Partial<Record<FunnelKind, string | null>>>) => Insight | null;

const funilPausado: Gerador = (resumo) => {
  const pausado = resumo.funnels.find((f) => !f.active);
  if (pausado === undefined) {
    return null;
  }
  return {
    id: `pausado-${pausado.kind}`,
    prioridade: 0,
    tone: 'critical',
    icone: 'pausa',
    titulo: `Funil de ${NOME_DO_FUNIL[pausado.kind]} desligado`,
    texto: `${formatCount(pausado.enteredCount)} entraram no período e ninguém foi contatado. Cada dia desligado é recuperação que não acontece.`,
    acao: { rotulo: 'Religar nas configurações', rota: '/configuracoes' },
  };
};

const respostasComOComercial: Gerador = (_resumo, agora) => {
  if (agora === null || agora.withSellerCount === 0) {
    return null;
  }
  return {
    id: 'com-comercial',
    prioridade: 1,
    tone: 'warning',
    icone: 'resposta',
    titulo: `${formatCount(agora.withSellerCount)} ${agora.withSellerCount === 1 ? 'pessoa respondeu e espera' : 'pessoas responderam e esperam'} o comercial`,
    texto: 'Quem responde sai da régua e vira venda da vendedora, mas só conta como recuperada em até 7 dias depois do contato. Resposta parada esfria.',
    acao: { rotulo: 'Ver contatos', rota: '/campanhas' },
  };
};

const altoValorParado: Gerador = (resumo, agora) => {
  const abertos = resumo.funnels.reduce((acc, f) => acc + f.openCount, 0);
  const parado = resumo.funnels.reduce((acc, f) => acc + f.openAmountCents, 0);
  if (abertos === 0 || parado === 0) {
    return null;
  }
  const alto = agora?.highValueOpen ?? null;
  const temAltoValor = alto !== null && alto.count > 0;
  return {
    id: 'valor-parado',
    prioridade: temAltoValor ? 1 : 3,
    tone: temAltoValor ? 'accent' : 'neutral',
    icone: 'moeda',
    titulo: `${formatBRLFromCents(parado)} ainda dão para recuperar`,
    texto: temAltoValor
      ? `${formatCount(abertos)} leads em aberto; ${formatCount(alto.count)} deles acima de ${formatBRLFromCents(alto.thresholdCents)} somam ${formatBRLFromCents(alto.amountCents)}. Um contato humano nesses vale mais que qualquer ajuste de texto.`
      : `${formatCount(abertos)} leads que já saíram e ainda estão dentro da janela. A régua continua; acompanhe quem responde.`,
    acao: { rotulo: 'Ver leads em aberto', rota: '/leads' },
  };
};

function taxaDaOferta(o: OfferSummary): number {
  return o.enteredCount === 0 ? 0 : Math.round((o.recoveredCount * 10_000) / o.enteredCount);
}

const ofertaQueSangra: Gerador = (resumo) => {
  const totalEntradas = resumo.offers.reduce((acc, o) => acc + o.enteredCount, 0);
  if (totalEntradas < 20) {
    return null;
  }
  // So ofertas com peso: pelo menos 5 entradas e 5% do total.
  const relevantes = resumo.offers.filter((o) => o.enteredCount >= 5 && o.enteredCount * 20 >= totalEntradas);
  if (relevantes.length < 2) {
    return null;
  }
  const pior = [...relevantes].sort((a, b) => taxaDaOferta(a) - taxaDaOferta(b) || b.enteredCount - a.enteredCount)[0];
  if (pior === undefined) {
    return null;
  }
  const funil = resumo.funnels.find((f) => f.kind === pior.kind);
  const mediaBps = funil === undefined ? resumo.total.recoveryRateBps : funil.recoveryRateBps;
  const taxaBps = taxaDaOferta(pior);
  // So e insight se estiver claramente abaixo da media do proprio funil (70% dela ou menos).
  if (mediaBps === 0 || taxaBps * 10 > mediaBps * 7) {
    return null;
  }
  return {
    id: `oferta-${pior.offerId}`,
    prioridade: 2,
    tone: 'warning',
    icone: 'alerta',
    titulo: `«${pior.name}» concentra entradas e recupera pouco`,
    texto: `${formatRatio(pior.enteredCount, totalEntradas)} de quem entrou no período veio desta oferta, e só ${formatPercentFromBps(taxaBps)} voltaram (média do funil: ${formatPercentFromBps(mediaBps)}). ${formatBRLFromCents(pior.openAmountCents)} parados nela. Vale conferir o checkout e o texto do 1º toque.`,
    acao: { rotulo: 'Abrir por oferta', rota: ROTA_DO_FUNIL[pior.kind] },
  };
};

const taxaMudou: Gerador = (resumo) => {
  const anterior = resumo.previousTotal;
  if (anterior === null) {
    return null;
  }
  const de = formatPercentFromBps(anterior.recoveryRateBps);
  const para = formatPercentFromBps(resumo.total.recoveryRateBps);
  if (caiuEmPontos(resumo.total.recoveryRateBps, anterior.recoveryRateBps, QUEDA_TOTAL_BPS)) {
    return {
      id: 'taxa-caiu',
      prioridade: 2,
      tone: 'critical',
      icone: 'grafico',
      titulo: `Taxa de recuperação caiu de ${de} para ${para}`,
      texto: `Em relação ao período anterior de mesmo tamanho. Se a queda está em um funil só, o sinal de saúde dele acima aponta qual.`,
      acao: null,
    };
  }
  if (subiuEmPontos(resumo.total.recoveryRateBps, anterior.recoveryRateBps, ALTA_TOTAL_BPS)) {
    return {
      id: 'taxa-subiu',
      prioridade: 6,
      tone: 'good',
      icone: 'estrela',
      titulo: `Taxa de recuperação subiu de ${de} para ${para}`,
      texto: 'O que mudou na régua neste período vale repetir. Anote antes de mexer de novo.',
      acao: null,
    };
  }
  return null;
};

const semResponsavel: Gerador = (resumo, _agora, responsaveis) => {
  const orfao = resumo.funnels.find((f) => f.active && f.enteredCount > 0 && (responsaveis[f.kind] ?? f.assigneeId) === null);
  if (orfao === undefined) {
    return null;
  }
  return {
    id: `sem-responsavel-${orfao.kind}`,
    prioridade: 3,
    tone: 'warning',
    icone: 'usuarios',
    titulo: `Funil de ${NOME_DO_FUNIL[orfao.kind]} sem responsável`,
    texto: `${formatCount(orfao.openCount)} pessoas em aberto e ninguém designado para receber quem responde. Escolha alguém no seletor da linha do funil.`,
    acao: null,
  };
};

const melhorHorario: Gerador = (resumo) => {
  const melhor = resumo.bestHour;
  if (melhor === null || melhor.calls < 10) {
    return null;
  }
  return {
    id: 'melhor-horario',
    prioridade: 4,
    tone: 'info',
    icone: 'relogio',
    titulo: `A IA é mais atendida entre ${faixaDaHora(melhor.hour)}`,
    texto: `${formatRatio(melhor.answered, melhor.calls)} de atendimento nessa faixa (${formatCount(melhor.answered)} de ${formatCount(melhor.calls)} ligações), a melhor do período. Vale concentrar a 2ª tentativa de ligação nesse horário.`,
    acao: { rotulo: 'Ajustar a régua', rota: '/configuracoes' },
  };
};

const canalCampeao: Gerador = (resumo) => {
  const comVenda = resumo.channels.filter((c) => c.recoveredCount > 0);
  if (comVenda.length < 2 || resumo.total.recoveredAmountCents === 0) {
    return null;
  }
  const ordenados = [...comVenda].sort((a, b) => b.recoveredAmountCents - a.recoveredAmountCents);
  const primeiro = ordenados[0];
  const segundo = ordenados[1];
  if (primeiro === undefined || segundo === undefined) {
    return null;
  }
  const ticket = (c: typeof primeiro): number => Math.round(c.recoveredAmountCents / c.recoveredCount);
  return {
    id: 'canal-campeao',
    prioridade: 5,
    tone: 'neutral',
    icone: 'raio',
    titulo: `${CHANNEL_LABEL_FULL[primeiro.channel]} trouxe ${formatRatio(primeiro.recoveredAmountCents, resumo.total.recoveredAmountCents)} do recuperado`,
    texto: `${formatCount(primeiro.recoveredCount)} vendas com ticket médio de ${formatBRLFromCents(ticket(primeiro))}; ${CHANNEL_LABEL_FULL[segundo.channel]} vem em seguida com ${formatCount(segundo.recoveredCount)} vendas a ${formatBRLFromCents(ticket(segundo))}. Cada venda é do último contato antes do pagamento.`,
    acao: null,
  };
};

const GERADORES: readonly Gerador[] = [funilPausado, respostasComOComercial, altoValorParado, ofertaQueSangra, taxaMudou, semResponsavel, melhorHorario, canalCampeao];

export function gerarInsights(
  resumo: FunnelsSummary,
  agora: LiveSnapshot | null,
  responsaveis: Readonly<Partial<Record<FunnelKind, string | null>>>,
): readonly Insight[] {
  const lista = GERADORES.map((g) => g(resumo, agora, responsaveis)).filter((i): i is Insight => i !== null);
  return lista.sort((a, b) => a.prioridade - b.prioridade).slice(0, MAXIMO_DE_INSIGHTS);
}
